import { ChipInput } from '@/components/ChipInput';
import MonthCalendarPopover from '@/components/MonthCalendarPopover';
import { formatDateChip, textInput } from '@/features/reservations/model/reservation-form';
import { useT } from '@/i18n';
import { todayString } from '@/utils/datetime';
import { Block, List, ListInput, ListItem, Stepper } from 'framework7-react';
import { useRef, useState } from 'react';

interface Field<T> {
  value: T;
  onChange: (value: T) => void;
}

interface Props {
  date: Field<string>;
  time: Field<string>;
  partySize: Field<number>;
  duration?: Field<string>;
  maxPartySize: number;
  partyOverLimit: boolean;
  showSlotFull: boolean;
}

const DATE_PILL_STYLE = {
  background: 'rgba(120,120,128,0.12)',
  borderRadius: 6,
  padding: '4px 10px',
  border: 'none',
  cursor: 'pointer',
  fontSize: 17,
  lineHeight: '24px',
  color: 'var(--f7-theme-color)',
  whiteSpace: 'nowrap',
} as const;

const WARNING_STYLE = { marginTop: 8, color: 'var(--f7-text-color-warning, #b25e00)' };
const WARNING_ICON_STYLE = { fontSize: 16, verticalAlign: '-3px', marginRight: 6 };

export default function BookingWhenList({
  date,
  time,
  partySize,
  duration,
  maxPartySize,
  partyOverLimit,
  showSlotFull,
}: Props) {
  const t = useT();
  const [calOpen, setCalOpen] = useState(false);
  const calBtnRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <List strongIos insetIos dividersIos>
        <ListItem noChevron title={t.editReservation.date}>
          <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
            calendar
          </i>
          {/* Tapping the date pill opens the month-calendar popout (same picker
              as the reservations date strip), instead of the native date input. */}
          <button
            ref={calBtnRef}
            slot="after"
            type="button"
            className="no-ripple"
            aria-label={t.editReservation.date}
            onClick={() => setCalOpen(true)}
            style={DATE_PILL_STYLE}
          >
            {formatDateChip(date.value)}
          </button>
        </ListItem>
        <ListItem noChevron title={t.editReservation.time}>
          <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
            clock
          </i>
          <ChipInput
            slot="after"
            type="time"
            step={1}
            value={time.value}
            onChange={time.onChange}
          />
        </ListItem>
        <ListItem noChevron title={t.editReservation.partySize}>
          <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
            person_2
          </i>
          {/* Party size isn't hard-capped at maxPartySize: staff handle large
              parties that online booking turns away ("please call us"). We let
              them exceed the limit but warn below - the cap stays well above it. */}
          <Stepper
            slot="after"
            value={partySize.value}
            min={1}
            max={Math.max(maxPartySize + 10, 20)}
            onStepperChange={(val: number) => partySize.onChange(val)}
            small
            raisedMd
          />
        </ListItem>
        {duration && (
          <ListInput
            label={t.editReservation.duration}
            type="number"
            placeholder="60"
            {...textInput(duration)}
          />
        )}
      </List>

      {partyOverLimit && (
        <Block className="rsv-party-warning" style={WARNING_STYLE}>
          <i className="f7-icons" style={WARNING_ICON_STYLE}>
            exclamationmark_triangle
          </i>
          {/* Staff-facing only - the configured largePartyMsg ("please call us")
              is guest copy for online booking, so it's intentionally not shown. */}
          {t.createReservation.partySizeOverLimit(maxPartySize)}
        </Block>
      )}

      {showSlotFull && (
        <Block className="rsv-slot-warning" style={WARNING_STYLE}>
          <i className="f7-icons" style={WARNING_ICON_STYLE}>
            exclamationmark_triangle
          </i>
          {t.createReservation.slotFullWarning}
        </Block>
      )}

      <MonthCalendarPopover
        open={calOpen}
        onClose={() => setCalOpen(false)}
        anchorEl={calBtnRef.current}
        selectedDate={date.value}
        minDate={todayString()}
        onSelect={(d) => {
          date.onChange(d);
          setCalOpen(false);
        }}
      />
    </>
  );
}
